import { useState } from 'react';
import { X, RotateCcw, CheckCircle } from 'lucide-react';
import { clearAllUiPreferences } from '../lib/uiPreferences';

interface PreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function PreferencesModal({ isOpen, onClose }: PreferencesModalProps) {
  const [confirming, setConfirming] = useState(false);
  const [justReset, setJustReset] = useState(false);

  if (!isOpen) return null;

  const handleReset = () => {
    clearAllUiPreferences();
    setConfirming(false);
    setJustReset(true);
    setTimeout(() => setJustReset(false), 2500);
  };

  const handleClose = () => {
    setConfirming(false);
    setJustReset(false);
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={handleClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Preferences</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close preferences"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-1">Saved interface choices</h3>
              <p className="text-sm text-gray-600">
                We remember small things in your browser, like which sections you've expanded on the
                results page, so the app feels the way you left it. Reset them whenever you want a
                clean slate.
              </p>
            </div>

            {justReset ? (
              <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-lg p-4">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-900">Preferences reset</p>
                  <p className="text-sm text-green-700">
                    All remembered interface choices have been cleared.
                  </p>
                </div>
              </div>
            ) : confirming ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                <p className="text-sm text-gray-800">
                  Reset all remembered interface preferences? Expanded sections on the results page
                  will collapse back to their defaults.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleReset}
                    className="bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Yes, reset preferences
                  </button>
                  <button
                    onClick={() => setConfirming(false)}
                    className="bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setConfirming(true)}
                className="w-full flex items-center justify-center gap-2 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-lg px-4 py-3 text-sm font-medium transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Reset preferences
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
